
import { Lead } from '@/types/lead';
import { supabase } from '@/integrations/supabase/client';
import { mapDbLeadToLead } from '../mappers/leadMapper';
import { SortBy, LocationFilter, ContactStatusFilter } from '@/types/filters';

export interface GetLeadsPageParams {
  companyId: string;
  page?: number;
  pageSize?: number;
  selectedUserId?: string | null;
  currentUserId?: string | null;
  showFavoritesOnly?: boolean;
  selectedCategories?: string[];
  selectedSector?: string | null;
  searchQuery?: string;
  sortBy?: SortBy;
  selectedLocation?: string | null;
  locationFilter?: LocationFilter | null;
  hasEmail?: boolean;
  contactStatus?: ContactStatusFilter;
  showDuplicatesOnly?: boolean;
}

export interface LeadsPageResult {
  leads: Lead[];
  totalCount: number;
}

/**
 * Build the location filter clause for a Supabase query.
 * Returns an `.or()` filter string or null.
 */
export function buildLocationFilterString(locationFilter?: LocationFilter | null, selectedLocation?: string | null): string | null {
  if (locationFilter?.type === 'preset' && locationFilter.preset?.postcodes?.length) {
    return locationFilter.preset.postcodes.map(pc => `address.ilike.%${pc}%`).join(',');
  }
  if (locationFilter?.type === 'custom' && locationFilter.customText?.trim()) {
    return `address.ilike.%${locationFilter.customText.trim()}%`;
  }
  // Legacy fallback
  if (selectedLocation?.trim()) {
    return `address.ilike.%${selectedLocation.trim()}%`;
  }
  return null;
}

export const getLeadsPage = async (params: GetLeadsPageParams): Promise<LeadsPageResult> => {
  const {
    companyId,
    page = 1,
    pageSize: rawPageSize = 50,
    selectedUserId = null,
    currentUserId = null,
    showFavoritesOnly = false,
    selectedCategories = [],
    selectedSector = null,
    searchQuery = '',
    sortBy = 'newest',
    selectedLocation = null,
    locationFilter = null,
    hasEmail = false,
    contactStatus = 'all',
    showDuplicatesOnly = false,
  } = params;

  if (!companyId) {
    return { leads: [], totalCount: 0 };
  }

  const pageSize = Math.min(Math.max(rawPageSize, 1), 250);
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;

  // Determine sort column and direction
  let sortColumn = 'created_at';
  let sortAscending = false;
  switch (sortBy) {
    case 'oldest': sortColumn = 'created_at'; sortAscending = true; break;
    case 'newest': sortColumn = 'created_at'; sortAscending = false; break;
    case 'review_score': sortColumn = 'rating'; sortAscending = false; break;
    case 'name_asc': sortColumn = 'name'; sortAscending = true; break;
    case 'name_desc': sortColumn = 'name'; sortAscending = false; break;
    case 'last_contacted': sortColumn = 'last_contacted_at'; sortAscending = false; break;
    case 'last_contacted_asc': sortColumn = 'last_contacted_at'; sortAscending = true; break;
    // quality sorts handled client-side since they use JSON field
    default: sortColumn = 'created_at'; sortAscending = false;
  }

  try {
    // If duplicates filter is active, fetch duplicate IDs first
    let duplicateIds: string[] | null = null;
    if (showDuplicatesOnly) {
      const { data: dupData, error: dupError } = await supabase.rpc('get_duplicate_lead_ids', {
        p_company_id: companyId,
      });
      if (dupError) {
        console.error('Error fetching duplicate lead IDs:', dupError);
        return { leads: [], totalCount: 0 };
      }
      duplicateIds = (dupData as string[]) || [];
      if (duplicateIds.length === 0) {
        return { leads: [], totalCount: 0 };
      }
    }

    let query = supabase
      .from('leads')
      .select(`
        id, name, address, category, email, phone, website, image_url,
        is_favorite, rating, review_count, status, latitude, longitude,
        web_search_status, web_search_queue_time, web_search_data,
        web_search_updated_at, web_search_retry_count, web_search_last_error,
        combined_data, combined_updated_at,
        deep_research_status, deep_research_updated_at,
        crawl_status, crawl_updated_at, sector, last_contacted_at,
        company_id, place_id, created_by, created_at, updated_at,
        notes:notes ( id, content, created_at, created_by, lead_id, tags, updated_at ),
        documents:documents ( id, name, url, type, description, lead_id, created_at, uploaded_by ),
        lead_images:lead_images ( id, image_url, is_primary, position, created_at ),
        lead_key_contacts:lead_key_contacts ( id, name, email, phone, position, is_primary, notes, created_at, created_by, linkedin_url, source )
      `, { count: 'exact' })
      .eq('company_id', companyId);

    // Duplicates filter — chunk .in() to avoid URL length limits
    if (duplicateIds && duplicateIds.length > 0) {
      if (duplicateIds.length <= 200) {
        query = query.in('id', duplicateIds);
      } else {
        // For very large sets, use first 200 — the RPC already scoped to company
        query = query.in('id', duplicateIds.slice(0, 200));
      }
    }

    // Apply user filter server-side
    if (selectedUserId === 'my-leads' && currentUserId) {
      query = query.eq('created_by', currentUserId);
    } else if (selectedUserId && selectedUserId !== 'my-leads') {
      query = query.eq('created_by', selectedUserId);
    }

    // Favorites filter
    if (showFavoritesOnly) {
      query = query.eq('is_favorite', true);
    }

    // Has email filter
    if (hasEmail) {
      query = query.not('email', 'is', null);
    }

    // Category filter (multi-select)
    if (selectedCategories.length > 0) {
      const categoryFilter = selectedCategories.map(c => `category.ilike.${c}`).join(',');
      query = query.or(categoryFilter);
    }

    // Sector filter
    if (selectedSector) {
      query = query.eq('sector', selectedSector);
    }

    // Search filter
    if (searchQuery.trim()) {
      query = query.or(`name.ilike.%${searchQuery.trim()}%,address.ilike.%${searchQuery.trim()}%,category.ilike.%${searchQuery.trim()}%`);
    }

    // Location filter (preset with multiple postcodes OR custom text)
    const locFilter = buildLocationFilterString(locationFilter, selectedLocation);
    if (locFilter) {
      query = query.or(locFilter);
    }

    // Contact status filter
    if (contactStatus === 'not_contacted') {
      query = query.is('last_contacted_at', null);
    } else if (contactStatus === 'contacted') {
      query = query.not('last_contacted_at', 'is', null);
    } else if (contactStatus === 'not_contacted_30') {
      const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
      query = query.or(`last_contacted_at.is.null,last_contacted_at.lt.${thirtyDaysAgo}`);
    } else if (contactStatus === 'not_contacted_60') {
      const sixtyDaysAgo = new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString();
      query = query.or(`last_contacted_at.is.null,last_contacted_at.lt.${sixtyDaysAgo}`);
    }

    // Sort and paginate
    query = query.order(sortColumn, { ascending: sortAscending, nullsFirst: false });
    query = query.range(from, to);

    const { data, error, count } = await query;

    if (error) {
      console.error('Error fetching leads page:', error);
      throw error;
    }

    const leads = (data || []).map((lead: any) => {
      const leadObj = mapDbLeadToLead(lead);
      return {
        ...leadObj,
        images: (lead.lead_images || []).map((img: any) => ({
          id: img.id,
          leadId: lead.id,
          imageUrl: img.image_url,
          isPrimary: img.is_primary,
          position: img.position,
          createdAt: img.created_at,
        })),
        keyContacts: (lead.lead_key_contacts || []).map((contact: any) => ({
          id: contact.id,
          leadId: lead.id,
          name: contact.name,
          email: contact.email,
          phone: contact.phone,
          position: contact.position,
          isPrimary: contact.is_primary,
          notes: contact.notes,
          createdAt: contact.created_at,
          createdBy: contact.created_by,
          linkedinUrl: contact.linkedin_url || undefined,
          source: contact.source || 'manual',
          status: contact.status || 'active',
        })),
      };
    });

    return { leads, totalCount: count ?? 0 };
  } catch (error) {
    console.error('Error in getLeadsPage:', error);
    return { leads: [], totalCount: 0 };
  }
};
